import { FC, useState } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Loader from '../../../components/loader/index';
import { Radio, RadioGroup } from '../../../components/radio-group/index';
import { Switch } from '../../../components/switch/index';
import { Table, TableRow } from '../../../components/table/index';
import { Tag } from '../../../components/tag/index';
import { QueryKey } from '../../../constants/index';
import { Company, useInvalidateQueries, User } from '../../../hooks/index';
import { NotificationSettingsType } from '../../../hooks/user/index';
import { StakeholderRole } from '../../../types/stakeholder.types';
import {
  importanceColor,
  importanceTitle,
  notificationTagDescription,
  notificationTagImportance,
  notificationTagTitle,
  notificationTagToUserNotificationSettings,
} from '../../../types/user.types';
import {
  NotificationSettingTab,
  notificationSettingTabTitleMap,
  NotificationTableColumn,
  notificationTableColumnMap,
  notificationTableColumnTitleMap,
  notificationTabTags,
} from './email-settings.types';

const notificationTableTitle = notificationTableColumnTitleMap.reduce(
  (acc, [key, titleKey]) => ({ ...acc, [key]: <AppFormattedMessage id={titleKey} /> }),
  {},
);

export const NotificationSettings: FC = () => {
  const { user, isLoading } = User.useUser();
  const { selectedCompany } = Company.useSelected();
  const [selectedTab, setTab] = useState(
    selectedCompany?.stakeholder?.role === StakeholderRole.STAKEHOLDER
      ? NotificationSettingTab.STAKEHOLDER
      : NotificationSettingTab.ADMIN,
  );
  const { update } = User.useUpdateNotificationSettings();
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_USER,
    QueryKey.GET_UNREAD_NOTIFICATION_COUNT,
  );

  const settings = {
    inApp: user?.inAppSettings,
    email: user?.emailSettings,
  };

  const handleTabChange = (tab: NotificationSettingTab) => {
    invalidateQuery();
    setTab(tab);
  };

  if (isLoading) return <Loader />;

  return (
    <div className="-mx-4 w-full overflow-x-auto">
      <div className="p-4 pt-0">
        <RadioGroup
          className="h-fit w-fit divide-x divide-gray-200 overflow-hidden rounded border border-gray-200"
          onChange={handleTabChange}
          value={selectedTab}
          wrapperClassName={'h-9'}
        >
          {selectedCompany?.stakeholder?.role === StakeholderRole.STAKEHOLDER ? (
            <Radio
              className="flex h-9 w-fit items-center justify-center px-4 text-sm font-[450] text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]"
              styleType="NONE"
              value={NotificationSettingTab.STAKEHOLDER}
            >
              <span>
                <AppFormattedMessage id="STAKEHOLDER" />
              </span>
            </Radio>
          ) : (
            <>
              {notificationSettingTabTitleMap.map(([key, titleKey]) => (
                <Radio
                  className="flex h-9 w-fit items-center justify-center px-4 text-sm font-[450] text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]"
                  key={key}
                  styleType="NONE"
                  value={key}
                >
                  <span>
                    <AppFormattedMessage id={titleKey} />
                  </span>
                </Radio>
              ))}
            </>
          )}
        </RadioGroup>
      </div>
      <Table
        className="max-lg:hidden"
        columnOrder={notificationTableColumnMap}
        columnsTitle={notificationTableTitle}
        tHeadClassNames={{
          thClassName: 'pl-4',
          labelClassName: 'font-medium whitespace-nowrap text-[#172335] text-label-md',
        }}
      >
        {notificationTabTags[selectedTab].map((key) => (
          <TableRow
            columnOrder={notificationTableColumnMap}
            contentClassName="pl-4 h-[72px]"
            fields={{
              id: '1',
              [NotificationTableColumn.EMAIL]: (
                <Switch
                  enabled={settings?.email?.[notificationTagToUserNotificationSettings[key]]}
                  key={`${selectedTab}-email-switch-${key}`}
                  onChange={(emailState) =>
                    update({
                      data: {
                        [notificationTagToUserNotificationSettings[key]]: emailState,
                      },
                      type: NotificationSettingsType.EMAIL,
                    })
                  }
                />
              ),
              [NotificationTableColumn.IMPORTANCE]: (
                <Tag
                  title={
                    <AppFormattedMessage id={importanceTitle[notificationTagImportance[key]]} />
                  }
                  variant={importanceColor[notificationTagImportance[key]]}
                />
              ),
              [NotificationTableColumn.IN_APP]: (
                <Switch
                  enabled={settings?.inApp?.[notificationTagToUserNotificationSettings[key]]}
                  key={`${selectedTab}-in-app-switch-${key}`}
                  onChange={(inAppState) =>
                    update({
                      data: {
                        [notificationTagToUserNotificationSettings[key]]: inAppState,
                      },
                      type: NotificationSettingsType.IN_APP,
                    })
                  }
                />
              ),
              [NotificationTableColumn.NOTIFICATION_DESCRIPTION]: (
                <AppFormattedMessage id={notificationTagDescription[key]} />
              ),
              [NotificationTableColumn.NOTIFICATION_TYPE]: (
                <AppFormattedMessage id={notificationTagTitle[key]} />
              ),
            }}
            key={key}
          />
        ))}
      </Table>

      <div className="flex h-fit w-full flex-wrap items-center justify-center gap-4 px-4 pt-4 lg:hidden">
        {notificationTabTags[selectedTab].map((key) => (
          <div className="flex h-fit w-full min-w-[300px] max-w-[440px] flex-col divide-y-[1px] divide-gray-100 rounded-md shadow-sm">
            <div className="flex items-center justify-between px-3 py-4">
              <span className="max-w-[90%] truncate text-sm font-[450] text-gray-700">
                <AppFormattedMessage id={notificationTagTitle[key]} />
              </span>
              <Tag
                title={<AppFormattedMessage id={importanceTitle[notificationTagImportance[key]]} />}
                variant={importanceColor[notificationTagImportance[key]]}
              />
            </div>
            <span className="px-3 py-4 text-xs text-gray-500">
              <AppFormattedMessage id={notificationTagDescription[key]} />
            </span>
            <div className="flex divide-x-[1px] divide-gray-100 px-3 py-4">
              <div className="flex w-[50%] items-center justify-center gap-2">
                <span className="text-sm font-normal text-gray-700">
                  <AppFormattedMessage id="EMAIL" />
                </span>
                <Switch
                  enabled={settings?.email?.[notificationTagToUserNotificationSettings[key]]}
                  key={`${selectedTab}-email-switch-${key}`}
                  onChange={(emailState) =>
                    update({
                      data: {
                        [notificationTagToUserNotificationSettings[key]]: emailState,
                      },
                      type: NotificationSettingsType.EMAIL,
                    })
                  }
                />
              </div>
              <div className="flex w-[50%] items-center justify-center gap-2">
                <span className="text-sm font-normal text-gray-700">
                  <AppFormattedMessage id="IN_APP" />
                </span>
                <Switch
                  enabled={settings?.inApp?.[notificationTagToUserNotificationSettings[key]]}
                  key={`${selectedTab}-in-app-switch-${key}`}
                  onChange={(inAppState) =>
                    update({
                      data: {
                        [notificationTagToUserNotificationSettings[key]]: inAppState,
                      },
                      type: NotificationSettingsType.IN_APP,
                    })
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
